"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export function PrivacyClient() {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.meazora.com";
  const domain = siteUrl.replace("https://", "").replace("http://", "");
  const [activeSection, setActiveSection] = useState("architecture");

  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const menuItems = [
    { id: "architecture", label: "1. Privacy by Architecture" },
    { id: "storage", label: "2. Browser-Only Storage" },
    { id: "cookies", label: "3. Cookies & Tracking" },
    { id: "analytics", label: "4. Analytics & Advertising" },
    { id: "ccpa", label: "5. Your Privacy Rights" },
    { id: "children", label: "6. Children's Privacy" },
    { id: "links", label: "7. Third-Party Links" },
    { id: "retention", label: "8. Data Retention" },
    { id: "contact", label: "9. Contact" },
  ];

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 md:px-6">
      {/* Back Link */}
      <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-300">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors group font-sans"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to Calculators
        </Link>
      </div>

      {/* Header */}
      <div className="border-b border-slate-200 dark:border-zinc-800 pb-8 mb-12 animate-in fade-in duration-500">
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
          Security Sandbox
        </span>
        <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-slate-55 mt-2 mb-4 tracking-tight leading-none font-sans">
          Privacy Policy — Meazora
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-zinc-500 font-mono">
          <span>Last updated: July 12, 2026</span>
          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-zinc-750" />
          <span>Read time: 3 min</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
        {/* Sticky Sidebar */}
        <aside className="lg:col-span-1 sticky top-24 hidden lg:block select-none">
          <nav className="flex flex-col gap-3 font-sans text-xs">
            <span className="text-[9px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest pl-3 mb-1">
              Sections
            </span>
            {menuItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`py-1.5 pl-3 border-l-2 text-left font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? "border-sky-600 dark:border-sky-450 text-sky-600 dark:text-sky-400 font-semibold"
                    : "border-transparent text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-16 font-sans text-slate-650 dark:text-zinc-300">
          <section id="architecture" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              1. Privacy by Architecture
            </h2>
            <p className="text-sm leading-relaxed">
              Our fundamental architecture is built on absolute client-side execution. Unlike traditional platforms that send your home measurements, room specifications, and project budgets to a backend server database, {siteName} tools calculate material volumes and project layouts entirely within your web browser sandbox.
            </p>
            <p className="text-sm leading-relaxed">
              This means we do not, and physically cannot, access, store, or view any of the numbers you enter. Your project profile remains completely local and invisible to us and any third parties.
            </p>
            <div className="border-l-2 border-sky-500/50 pl-4 py-1 my-4 bg-slate-50/50 dark:bg-zinc-900/30 rounded-r-lg text-xs text-slate-500 dark:text-zinc-400">
              No server databases, no cloud processing logs, and no financial or house detail collection. Everything executes locally on your device.
            </div>
          </section>

          <section id="storage" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              2. Browser-Only Storage
            </h2>
            <p className="text-sm leading-relaxed">
              To improve your user experience and prevent you from re-entering your planning parameters (such as standard waste percentages, room dimensions, or regional labor cost adjustments) every time you reload, we utilize your browser’s <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-[11px] font-mono text-slate-800 dark:text-zinc-200">localStorage</code>.
            </p>
            <p className="text-sm leading-relaxed">
              This data is stored purely locally within your browser sandbox. It is never synchronized to a server or shared. You can instantly delete all cached calculator states by clearing your browser cookies and site storage, or by resetting parameters directly inside the specific tool dashboards.
            </p>
          </section>

          <section id="cookies" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              3. Cookies &amp; Tracking
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} uses cookies and similar technologies for essential site functionality and basic analytics. We do not use tracking cookies for behavioral advertising, cross-site profiling, or building individual user dossiers.
            </p>
            <p className="text-sm leading-relaxed">
              You may disable cookies through your browser settings at any time. The site will continue to function without cookies; however, preferences saved in localStorage (such as calculator inputs) may not persist across sessions.
            </p>
          </section>

          <section id="analytics" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              4. Analytics &amp; Advertising
            </h2>
            <p className="text-sm leading-relaxed">
              We use Google Analytics 4 to monitor general website traffic trends — page visits, popular calculators, and geographic region (country-level only). Google Analytics may set its own cookies. Data collected is aggregated and anonymized. Google&rsquo;s privacy policy is available at <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 dark:text-sky-400 hover:underline">policies.google.com/privacy</a>.
            </p>
            <p className="text-sm leading-relaxed">
              We also display advertisements through Google AdSense, which may use cookies to serve interest-based ads. You can manage ad personalization through Google&rsquo;s Ad Settings at <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-sky-600 dark:text-sky-400 hover:underline">adssettings.google.com</a>. To learn more about how Google uses data, visit <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-sky-600 dark:text-sky-400 hover:underline">policies.google.com/technologies/partner-sites</a>.
            </p>
            <ul className="list-none pl-0 space-y-3 text-sm">
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500/70 mr-2.5 mt-2 shrink-0" />
                <span>We do not sell, trade, or share personal data with third-party advertisers.</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500/70 mr-2.5 mt-2 shrink-0" />
                <span>We do not log personal identifiers, IP addresses, or build individual visitor profiles.</span>
              </li>
            </ul>
          </section>

          <section id="ccpa" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              5. Your Privacy Rights
            </h2>
            <p className="text-sm leading-relaxed">
              Depending on your location, you may have rights under laws such as the California Consumer Privacy Act (CCPA), the California Privacy Rights Act (CPRA), or the General Data Protection Regulation (GDPR). These rights may include the right to know what personal information is collected, the right to request deletion of personal information, and the right to opt out of the sale or sharing of personal information.
            </p>
            <p className="text-sm leading-relaxed">
              Because {siteName} does not collect, store, or sell personal information, these rights are inherently satisfied by our architecture. If you have questions about exercising privacy rights, please contact us using the information below.
            </p>
          </section>

          <section id="children" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              6. Children&rsquo;s Privacy
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} is not directed to children under the age of 13, and we do not knowingly collect personal information from children. Our tools are designed for adults making independent planning decisions. If you believe a child has provided personal information through our site, please contact us so we can promptly remove it. We comply with the Children&rsquo;s Online Privacy Protection Act (COPPA).
            </p>
          </section>

          <section id="links" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              7. Third-Party Links
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} contains links to building standards databases, manufacturer calculation guides, and municipal zoning checklists. Once you follow a link away from our website, you are subject to the privacy terms of that destination site. We encourage you to read their policies.
            </p>
          </section>

          <section id="retention" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              8. Data Retention &amp; Deletion
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} does not maintain server-side databases of user data. Calculator inputs are stored exclusively in your browser&rsquo;s localStorage and are under your complete control. You may delete this data at any time by clearing your browser storage, resetting calculator parameters, or using your browser&rsquo;s privacy tools.
            </p>
            <p className="text-sm leading-relaxed">
              Aggregated, anonymized analytics data (page visit counts) is retained for standard reporting purposes and cannot be used to identify individual users.
            </p>
          </section>

          <section id="contact" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              9. Contact
            </h2>
            <p className="text-sm leading-relaxed">
              If you have questions or feedback regarding our privacy practices, please contact us directly at <a href={`mailto:support@${domain}`} className="text-sky-600 dark:text-sky-400 hover:underline font-mono">support@{domain}</a> or visit our <Link href="/contact/" className="text-sky-600 dark:text-sky-400 hover:underline">Contact Page</Link>. Our client-side code execution is open and transparent, and we welcome audits of our code.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
